import { User } from "@/core/services/api/interfaces/manager/User";
import {
  UserSettingsInterface,
  UserSettings,
  AttrKey,
  TablesAttrKey,
} from "@/core/services/api/interfaces/local/UserSettingsInterface";
import { ApprovedTransactionsColumns } from "@/core/services/api/interfaces/local/ApprovedTransactionsColumns";
import { PendingTransactionsColumns } from "@/core/services/api/interfaces/local/PendingTransactionsColumns";
import { ImportsColumns } from "@/core/services/api/interfaces/local/ImportsColumns";
import { ReportsColumns } from "@/core/services/api/interfaces/local/ReportsColumns";
import { ArchivesColumns } from "@/core/services/api/interfaces/local/ArchivesColumns";
import { Theme } from "@/store/enums/ThemeEnum";

export enum StorageKey {
  USER = "banking_user.identity",
  USER_SETTINGS = "banking_user.settings",
  LIVE_CHAT_SETTINGS = "banking.livechat",
}

const defaultSettings = new UserSettings({
  [AttrKey.TABLES]: {
    [TablesAttrKey.APPROVED_TRANSACTIONS]: {
      columns: [
        ApprovedTransactionsColumns.LOG_CREATED_AT,
        ApprovedTransactionsColumns.ID,
        ApprovedTransactionsColumns.TYPE,
        ApprovedTransactionsColumns.BANK_ACCOUNT,
        ApprovedTransactionsColumns.REMITTER_NAME,
        ApprovedTransactionsColumns.FROM_ACCOUNT,
        ApprovedTransactionsColumns.BENEFICIARY_NAME,
        ApprovedTransactionsColumns.TO_ACCOUNT,
        ApprovedTransactionsColumns.AMOUNT,
        ApprovedTransactionsColumns.STATUS,
        ApprovedTransactionsColumns.BALANCE,
        ApprovedTransactionsColumns.ACTIONS,
      ],
      per_page: 100,
    },
    [TablesAttrKey.ADMIN_ACTION_REQUIRE_TRANSACTIONS]: {
      columns: [
        ApprovedTransactionsColumns.LOG_CREATED_AT,
        ApprovedTransactionsColumns.ID,
        ApprovedTransactionsColumns.TYPE,
        ApprovedTransactionsColumns.BANK_ACCOUNT,
        ApprovedTransactionsColumns.REMITTER_NAME,
        ApprovedTransactionsColumns.FROM_ACCOUNT,
        ApprovedTransactionsColumns.BENEFICIARY_NAME,
        ApprovedTransactionsColumns.TO_ACCOUNT,
        ApprovedTransactionsColumns.AMOUNT,
        ApprovedTransactionsColumns.STATUS,
        ApprovedTransactionsColumns.BALANCE,
        ApprovedTransactionsColumns.ACTIONS,
      ],
      per_page: 100,
    },
    [TablesAttrKey.PENDING_TRANSACTIONS]: {
      columns: [
        PendingTransactionsColumns.CREATED_AT,
        PendingTransactionsColumns.ID,
        PendingTransactionsColumns.TYPE,
        PendingTransactionsColumns.BANK_ACCOUNT,
        PendingTransactionsColumns.REMITTER_NAME,
        PendingTransactionsColumns.FROM_ACCOUNT,
        PendingTransactionsColumns.BENEFICIARY_NAME,
        PendingTransactionsColumns.TO_ACCOUNT,
        PendingTransactionsColumns.AMOUNT,
        PendingTransactionsColumns.STATUS,
        PendingTransactionsColumns.ACTIONS,
      ],
      per_page: 100,
    },
    [TablesAttrKey.IMPORTS]: {
      columns: [
        ImportsColumns.BANK_ACCOUNT,
        ImportsColumns.USER,
        ImportsColumns.TRANSACTIONS_SUCCESS_COUNT,
        ImportsColumns.TRANSACTIONS_SUCCESS_AMOUNT,
        ImportsColumns.TRANSACTIONS_SUCCESS_AMOUNT_WITH_FEE,
        ImportsColumns.TRANSACTIONS_FAILED_COUNT,
        ImportsColumns.TRANSACTIONS_FAILED_AMOUNT,
        ImportsColumns.STATUS,
        ImportsColumns.CREATED_AT,
        ImportsColumns.ACTIONS,
      ],
      per_page: 100,
    },
    [TablesAttrKey.REPORTS]: {
      columns: [
        ReportsColumns.NAME,
        ReportsColumns.BANK_ACCOUNT,
        ReportsColumns.REPORT_PERIOD,
        ReportsColumns.STATUS,
        ReportsColumns.FORMAT,
        ReportsColumns.CREATED_AT,
        ReportsColumns.ACTIONS,
      ],
      per_page: 100,
    },
    [TablesAttrKey.ARCHIVES]: {
      columns: [
        ArchivesColumns.ID,
        ArchivesColumns.DATE_FROM,
        ArchivesColumns.DATE_TO,
        ArchivesColumns.FILENAME,
        ArchivesColumns.STATUS,
        ArchivesColumns.CREATED_AT,
        ArchivesColumns.ACTIONS,
      ],
      per_page: 100,
    },
  },
});

if (Theme.KATBIT === process.env.VUE_APP_THEME) {
  defaultSettings[AttrKey.TABLES][TablesAttrKey.APPROVED_TRANSACTIONS].columns =
    [
      ApprovedTransactionsColumns.LOG_CREATED_AT,
      ApprovedTransactionsColumns.ID,
      ApprovedTransactionsColumns.TYPE,
      ApprovedTransactionsColumns.AMOUNT,
      ApprovedTransactionsColumns.STATUS,
      ApprovedTransactionsColumns.REFERENCE_MESSAGE,
      ApprovedTransactionsColumns.BALANCE,
      ApprovedTransactionsColumns.ACTIONS,
    ];
  defaultSettings[AttrKey.TABLES][TablesAttrKey.PENDING_TRANSACTIONS].columns =
    [
      PendingTransactionsColumns.CREATED_AT,
      PendingTransactionsColumns.ID,
      PendingTransactionsColumns.TYPE,
      PendingTransactionsColumns.AMOUNT,
      PendingTransactionsColumns.STATUS,
      PendingTransactionsColumns.BANK_ACCOUNT,
      PendingTransactionsColumns.REFERENCE_MESSAGE,
      PendingTransactionsColumns.ACTIONS,
    ];
}

export const getUser = (): User | null => {
  const user = window.localStorage.getItem(StorageKey.USER);
  if (!user || "undefined" === user) {
    return null;
  }

  return JSON.parse(user);
};

export const getUserSettings = (): UserSettings => {
  const settings = window.localStorage.getItem(StorageKey.USER_SETTINGS);
  if (settings && "undefined" !== settings) {
    const userSettings = new UserSettings(JSON.parse(settings));

    const requiredKeys = Object.keys(TablesAttrKey);
    let isMissing = false;
    for (const key of requiredKeys) {
      if (key in userSettings[AttrKey.TABLES]) {
        continue;
      }
      userSettings[AttrKey.TABLES][key] = defaultSettings[AttrKey.TABLES][key];
      isMissing = true;
    }
    if (isMissing) {
      saveUserSettings(userSettings);
    }

    return userSettings;
  }

  saveUserSettings(defaultSettings);

  return defaultSettings;
};

export const saveUserSettings = (settings: UserSettingsInterface): void => {
  window.localStorage.setItem(
    StorageKey.USER_SETTINGS,
    JSON.stringify(settings)
  );
};

export const destroyUserSettings = (): void => {
  window.localStorage.removeItem(StorageKey.USER_SETTINGS);
};

export const saveUserSettingsSpecific = (
  settings: UserSettingsInterface
): void => {
  window.localStorage.setItem(
    StorageKey.USER_SETTINGS,
    JSON.stringify(settings)
  );
};

/**
 * @description save token into localStorage
 */
export const saveUser = (user: User): void => {
  window.localStorage.setItem(StorageKey.USER, JSON.stringify(user));
};

export const destroyLiveChatSettings = (): void => {
  window.localStorage.removeItem(StorageKey.LIVE_CHAT_SETTINGS);
};

/**
 * @description remove token form localStorage
 */
export const destroyUser = (): void => {
  window.localStorage.removeItem(StorageKey.USER);
};

export default {
  getUser,
  saveUser,
  destroyUser,
  getUserSettings,
  saveUserSettings,
  destroyUserSettings,
  saveUserSettingsSpecific,
  destroyLiveChatSettings,
};
